import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  MapPin,
  Building2,
  Phone,
  Mail,
  ExternalLink,
  Search,
  Filter,
  Users,
  Loader2,
} from "lucide-react";
import api from "../lib/api";
import "leaflet/dist/leaflet.css";

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icons by channel
const createChannelIcon = (color) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background-color: ${color};
      width: 30px;
      height: 30px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        transform: rotate(45deg);
        color: white;
        font-size: 14px;
        font-weight: bold;
      ">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        </svg>
      </div>
    </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });
};

const CHANNEL_COLORS = {
  "Retail": "#3b82f6",
  "GDO": "#10b981",
  "DO": "#8b5cf6",
  "Ho.Re.Ca.": "#f59e0b",
  "Distributore": "#ef4444",
};

// Italian cities coordinates database
const ITALIAN_CITIES = {
  // Major cities
  "roma": [41.9028, 12.4964],
  "milano": [45.4642, 9.1900],
  "napoli": [40.8518, 14.2681],
  "torino": [45.0703, 7.6869],
  "palermo": [38.1157, 13.3615],
  "genova": [44.4056, 8.9463],
  "bologna": [44.4949, 11.3426],
  "firenze": [43.7696, 11.2558],
  "bari": [41.1171, 16.8719],
  "catania": [37.5079, 15.0830],
  "venezia": [45.4408, 12.3155],
  "verona": [45.4384, 10.9916],
  "messina": [38.1938, 15.5540],
  "padova": [45.4064, 11.8768],
  "trieste": [45.6495, 13.7768],
  "taranto": [40.4644, 17.2470],
  "brescia": [45.5416, 10.2118],
  "parma": [44.8015, 10.3279],
  "prato": [43.8777, 11.1020],
  "modena": [44.6471, 10.9252],
  "reggio calabria": [38.1113, 15.6474],
  "reggio emilia": [44.6989, 10.6297],
  "perugia": [43.1107, 12.3908],
  "livorno": [43.5485, 10.3106],
  "ravenna": [44.4184, 12.2035],
  "cagliari": [39.2238, 9.1217],
  "foggia": [41.4621, 15.5444],
  "rimini": [44.0678, 12.5695],
  "salerno": [40.6824, 14.7681],
  "ferrara": [44.8381, 11.6199],
  "sassari": [40.7259, 8.5556],
  "latina": [41.4676, 12.9037],
  "giugliano in campania": [40.9279, 14.1956],
  "monza": [45.5845, 9.2744],
  "siracusa": [37.0755, 15.2866],
  "pescara": [42.4618, 14.2161],
  "bergamo": [45.6983, 9.6773],
  "forlì": [44.2227, 12.0407],
  "trento": [46.0748, 11.1217],
  "vicenza": [45.5455, 11.5354],
  "terni": [42.5636, 12.6427],
  "bolzano": [46.4983, 11.3548],
  "novara": [45.4469, 8.6220],
  "piacenza": [45.0526, 9.6930],
  "ancona": [43.6158, 13.5189],
  "andria": [41.2314, 16.2943],
  "arezzo": [43.4633, 11.8798],
  "udine": [46.0711, 13.2346],
  "cesena": [44.1396, 12.2464],
  "lecce": [40.3515, 18.1750],
  "pesaro": [43.9096, 12.9131],
  "barletta": [41.3145, 16.2826],
  "alessandria": [44.9071, 8.6115],
  "la spezia": [44.1025, 9.8240],
  "catanzaro": [38.9098, 16.5877],
  "pistoia": [43.9304, 10.9139],
  "brindisi": [40.6327, 17.9416],
  "cosenza": [39.3001, 16.2533],
  "lucca": [43.8430, 10.5027],
  "como": [45.8081, 9.0852],
  "grosseto": [42.7635, 11.1124],
  "caserta": [41.0742, 14.3325],
  "potenza": [40.6404, 15.8050],
  "varese": [45.8206, 8.8257],
  "campobasso": [41.5603, 14.6626],
  "avellino": [40.9146, 14.7906],
  "asti": [44.9000, 8.2067],
  "ragusa": [36.9269, 14.7326],
  "crotone": [39.0809, 17.1172],
  "pavia": [45.1847, 9.1582],
  "vibo valentia": [38.6759, 16.1000],
  "l'aquila": [42.3498, 13.3995],
  "enna": [37.5673, 14.2754],
  "caltanissetta": [37.4901, 14.0624],
  "trapani": [38.0174, 12.5365],
  "agrigento": [37.3111, 13.5766],
  "nuoro": [40.3216, 9.3312],
  "oristano": [39.9062, 8.5926],
  "belluno": [46.1403, 12.2167],
  "rovigo": [45.0703, 11.7897],
  "pordenone": [45.9564, 12.6615],
  "gorizia": [45.9413, 13.6221],
  "aosta": [45.7375, 7.3151],
  "isernia": [41.5940, 14.2329],
  "matera": [40.6664, 16.6043],
  "rieti": [42.4037, 12.8578],
  "viterbo": [42.4173, 12.1046],
  "frosinone": [41.6397, 13.3527],
  "benevento": [41.1297, 14.7779],
  "ascoli piceno": [42.8538, 13.5749],
  "fermo": [43.1608, 13.7155],
  "macerata": [43.2984, 13.4534],
  "massa": [44.0353, 10.1396],
  "siena": [43.3188, 11.3308],
  "pisa": [43.7228, 10.4017],
  "mantova": [45.1564, 10.7914],
  "cremona": [45.1333, 10.0234],
  "lodi": [45.3138, 9.5034],
  "lecco": [45.8566, 9.3976],
  "sondrio": [46.1699, 9.8715],
  "biella": [45.5628, 8.0551],
  "vercelli": [45.3220, 8.4186],
  "cuneo": [44.3905, 7.5483],
  "verbania": [45.9217, 8.5519],
  "savona": [44.3091, 8.4772],
  "imperia": [43.8895, 8.0283],
  "treviso": [45.6669, 12.2430],
  
  // Regional capitals (as fallback)
  "lazio": [41.9028, 12.4964],
  "lombardia": [45.4642, 9.1900],
  "campania": [40.8518, 14.2681],
  "piemonte": [45.0703, 7.6869],
  "sicilia": [37.5994, 14.0154],
  "liguria": [44.4056, 8.9463],
  "emilia-romagna": [44.4949, 11.3426],
  "emilia romagna": [44.4949, 11.3426],
  "toscana": [43.7696, 11.2558],
  "puglia": [41.1171, 16.8719],
  "veneto": [45.4408, 12.3155],
  "calabria": [38.9098, 16.5877],
  "sardegna": [39.2238, 9.1217],
  "abruzzo": [42.3498, 13.3995],
  "marche": [43.6158, 13.5189],
  "friuli-venezia giulia": [45.6495, 13.7768],
  "friuli venezia giulia": [45.6495, 13.7768],
  "umbria": [43.1107, 12.3908],
  "trentino-alto adige": [46.0748, 11.1217],
  "trentino alto adige": [46.0748, 11.1217],
  "basilicata": [40.6404, 15.8050],
  "molise": [41.5603, 14.6626],
  "valle d'aosta": [45.7375, 7.3151],
};

// Function to get coordinates for a client
const getClientCoordinates = (client) => {
  const city = (client.city || "").toLowerCase().trim();
  const region = (client.region || "").toLowerCase().trim();
  
  // Try city first
  if (city && ITALIAN_CITIES[city]) {
    // Add small random offset to prevent marker overlap
    const offset = () => (Math.random() - 0.5) * 0.02;
    return [ITALIAN_CITIES[city][0] + offset(), ITALIAN_CITIES[city][1] + offset()];
  }
  
  // Try region as fallback
  if (region && ITALIAN_CITIES[region]) {
    const offset = () => (Math.random() - 0.5) * 0.1;
    return [ITALIAN_CITIES[region][0] + offset(), ITALIAN_CITIES[region][1] + offset()];
  }
  
  return null;
};

// Component to fit map bounds
function FitBounds({ clients }) {
  const map = useMap();
  
  useEffect(() => {
    if (clients.length > 0) {
      const coords = clients
        .map(c => c.coordinates)
        .filter(c => c !== null);
      
      if (coords.length > 0) {
        const bounds = L.latLngBounds(coords);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [clients, map]);
  
  return null;
}

export default function ClientsMapPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      // Add coordinates to each client
      const clientsWithCoords = response.data.map(client => ({
        ...client,
        coordinates: getClientCoordinates(client),
      }));
      setClients(clientsWithCoords);
    } catch (error) {
      toast.error("Errore nel caricamento dei clienti");
    } finally {
      setLoading(false);
    }
  };

  // Get unique regions for filter
  const regions = useMemo(() => {
    const uniqueRegions = [...new Set(clients.map(c => c.region).filter(Boolean))];
    return uniqueRegions.sort();
  }, [clients]);

  // Filter clients
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesSearch = !searchTerm || 
        client.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.city?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesChannel = channelFilter === "all" || client.channel === channelFilter;
      const matchesRegion = regionFilter === "all" || client.region === regionFilter;
      return matchesSearch && matchesChannel && matchesRegion && client.coordinates;
    });
  }, [clients, searchTerm, channelFilter, regionFilter]);

  // Stats
  const stats = useMemo(() => {
    const mapped = clients.filter(c => c.coordinates).length;
    const total = clients.length;
    return { mapped, total, unmapped: total - mapped };
  }, [clients]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="clients-map-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold flex items-center gap-2">
            <MapPin className="w-8 h-8 text-accent" />
            Mappa Clienti
          </h1>
          <p className="text-muted-foreground">
            Distribuzione geografica dei clienti in Italia
          </p>
        </div>
        
        {/* Stats */}
        <div className="flex gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-accent">{stats.mapped}</div>
            <div className="text-xs text-muted-foreground">Mappati</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-muted-foreground">{stats.unmapped}</div>
            <div className="text-xs text-muted-foreground">Non mappati</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca cliente o città..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Canale" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i canali</SelectItem>
                <SelectItem value="Retail">Retail / Normal Trade</SelectItem>
                <SelectItem value="GDO">GDO</SelectItem>
                <SelectItem value="DO">DO</SelectItem>
                <SelectItem value="Ho.Re.Ca.">Ho.Re.Ca.</SelectItem>
                <SelectItem value="Distributore">Distributore</SelectItem>
              </SelectContent>
            </Select>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Regione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le regioni</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region} value={region}>{region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
            <span className="text-sm text-muted-foreground">Legenda:</span>
            {Object.entries(CHANNEL_COLORS).map(([channel, color]) => (
              <div key={channel} className="flex items-center gap-1">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: color }}
                />
                <span className="text-xs">{channel === "Retail" ? "Retail / Normal Trade" : channel}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Map */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div style={{ height: "calc(100vh - 350px)", minHeight: "500px" }}>
            <MapContainer
              center={[42.5, 12.5]}
              zoom={6}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              <FitBounds clients={filteredClients} />
              
              {filteredClients.map((client) => (
                <Marker
                  key={client.id}
                  position={client.coordinates}
                  icon={createChannelIcon(CHANNEL_COLORS[client.channel] || "#6b7280")}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <h3 className="font-bold text-base mb-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {client.company_name}
                      </h3>
                      
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            style={{ 
                              borderColor: CHANNEL_COLORS[client.channel],
                              color: CHANNEL_COLORS[client.channel]
                            }}
                          >
                            {client.channel === "Retail" ? "Retail / Normal Trade" : client.channel}
                          </Badge>
                        </div>
                        
                        <p className="text-muted-foreground">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {client.city}, {client.region}
                        </p>
                        
                        {client.contact_name && (
                          <p className="text-muted-foreground">
                            <Users className="w-3 h-3 inline mr-1" />
                            {client.contact_name}
                          </p>
                        )}
                        
                        {client.phone && (
                          <p className="text-muted-foreground">
                            <Phone className="w-3 h-3 inline mr-1" />
                            {client.phone}
                          </p>
                        )}
                      </div>
                      
                      <Button 
                        size="sm" 
                        className="w-full mt-3 bg-accent hover:bg-accent/90"
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Vai alla Scheda
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </CardContent>
      </Card>

      {/* Info about unmapped clients */}
      {stats.unmapped > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          {stats.unmapped} clienti non sono visualizzati sulla mappa perché la loro città non è stata riconosciuta.
        </p>
      )}
    </div>
  );
}
